import React, { useState } from "react";
import { MailWarning } from "lucide-react";
import { http, errMsg } from "@/lib/api";
import { toast } from "sonner";

/** Shown across the Portal whenever the signed-in user hasn't verified
 * their email yet — device activation and team invites are gated on
 * this server-side (routers/licensing.py, routers/users.py), so the
 * banner explains why those actions will 403 before the user hits it. */
export default function EmailVerifyBanner({ user }) {
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    if (!user || user.email_verified) return null;

    const send = async () => {
        setSending(true);
        try {
            const { data } = await http.post("/auth/send-verification");
            setSent(true);
            toast.success(
                data.email_delivery === "sent"
                    ? "Verification email sent — check your inbox."
                    : "Verification email logged (email delivery isn't configured in this environment)."
            );
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setSending(false);
        }
    };

    return (
        <div
            data-testid="portal-verify-email-banner"
            className="flex flex-wrap items-center gap-3 border-b border-amber-500/40 bg-amber-500/[0.08] px-6 py-2.5 text-xs text-amber-700 dark:text-amber-300"
        >
            <MailWarning size={14} />
            <span className="flex-1">
                Verify your email to activate devices and invite teammates.
            </span>
            <button
                onClick={send}
                disabled={sending || sent}
                data-testid="portal-verify-email-btn"
                className="rounded-md bg-amber-500/15 px-3 py-1 font-semibold uppercase tracking-widest text-amber-700 hover:bg-amber-500/25 disabled:opacity-50 dark:text-amber-200"
            >
                {sent ? "Sent" : sending ? "Sending…" : "Send verification email"}
            </button>
        </div>
    );
}
